import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type EditorialModalTone = "primary" | "success" | "danger" | "secondary";

interface EditorialModalProps {
  open?: boolean;
  isRTL?: boolean;
  onClose: () => void;
  /** Hero icon shown inside the disc next to the eyebrow. */
  heroIcon: LucideIcon;
  /** Mono uppercase eyebrow above the title (e.g. "STEP 01 / 02"). */
  eyebrow: string;
  /** Display title in the hero. */
  title: string;
  /** One-line subtitle in the hero. */
  subtitle?: string;
  /** Color tone driving the gradient. */
  tone?: EditorialModalTone;
  /** Sticky footer content — typically Cancel + Confirm pill buttons. */
  footer: React.ReactNode;
  /** Body content (numbered Section blocks). */
  children: React.ReactNode;
  /** Optional accessible label override for the close button. */
  closeLabel?: string;
  /** Max width tailwind class (defaults to max-w-lg). */
  maxWidthClass?: string;
}

const HERO_GRADIENT: Record<EditorialModalTone, string> = {
  primary: "from-primary to-blue-700",
  success: "from-success to-emerald-700",
  danger: "from-red-600 to-red-800",
  secondary: "from-secondary to-orange-600",
};

/**
 * Reusable editorial modal shell — gradient hero, scrollable body, sticky footer.
 * Mirrors the Top-up modal pattern.
 */
export function EditorialModal({
  open = true,
  isRTL = false,
  onClose,
  heroIcon: HeroIcon,
  eyebrow,
  title,
  subtitle,
  tone = "primary",
  footer,
  children,
  closeLabel = "Close",
  maxWidthClass = "max-w-lg",
}: EditorialModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        const first = focusable[0] ?? null;
        const last = focusable[focusable.length - 1] ?? null;
        if (e.shiftKey) {
          if (document.activeElement === first && last) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last && first) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        dir={isRTL ? "rtl" : "ltr"}
        className={cn(
          "relative w-full bg-card rounded-3xl shadow-2xl border border-border overflow-hidden max-h-[92vh] flex flex-col",
          maxWidthClass,
        )}
      >
        {/* Hero header */}
        <div
          className={cn(
            "relative bg-gradient-to-br text-white p-6 md:p-7 overflow-hidden",
            HERO_GRADIENT[tone],
          )}
        >
          <div
            aria-hidden
            className="absolute -top-12 -end-10 w-44 h-44 rounded-full bg-white/10"
          />
          <div
            aria-hidden
            className="absolute -bottom-16 -start-6 w-32 h-32 rounded-full bg-white/[0.06]"
          />

          <div className="relative flex items-start justify-between gap-3">
            <div className="space-y-3 min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
                  <HeroIcon className="w-4 h-4" strokeWidth={1.8} />
                </div>
                <span className="font-sans text-[10px] tracking-[0.25em] text-white/70 uppercase">
                  {eyebrow}
                </span>
              </div>
              <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">
                {title}
              </h2>
              {subtitle && (
                <p className="text-white/75 text-[13px]">{subtitle}</p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-xl border border-white/20 bg-white/10 hover:bg-white/20 transition-colors"
              aria-label={closeLabel}
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 md:p-7 space-y-6">
          {children}
        </div>

        {/* Sticky footer */}
        <div className="border-t border-border bg-card/95 backdrop-blur-sm p-4 md:p-5 flex items-center gap-3">
          {footer}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

interface ModalSectionProps {
  num: string;
  title: string;
  hint?: string;
  tone?: "primary" | "success" | "danger" | "secondary";
  children: React.ReactNode;
}

const SECTION_TONE: Record<NonNullable<ModalSectionProps["tone"]>, string> = {
  primary: "text-primary",
  success: "text-success",
  danger: "text-red-500",
  secondary: "text-secondary",
};

export function ModalSection({
  num,
  title,
  hint,
  tone = "primary",
  children,
}: ModalSectionProps) {
  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "font-sans text-[10px] tracking-[0.2em] uppercase",
            SECTION_TONE[tone],
          )}
        >
          {num} · {title}
        </span>
        {hint && (
          <span className="font-sans text-[10px] tracking-widest text-foreground-secondary/70 uppercase">
            {hint}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
